import React from "react";
import { motion } from "framer-motion";
import {
  Reveal,
  staggerContainer,
  revealUp,
  zoomIn,
} from "../common/Reveal";
import { useTheme } from "../../context/ThemeContext";

const ACADEMIC_PROGRAMMES = [
  {
    icon: "book",
    title: "Class 11 & 12 Commerce",
    text: "Strong conceptual foundations, systematic syllabus coverage, Board examination preparation, regular tests, revision and individual academic support.",
  },
  {
    icon: "award",
    title: "CA Foundation",
    text: "Concept-based teaching, structured syllabus coverage, examination-oriented practice, revision, regular assessments and performance analysis.",
  },
  {
    icon: "graduation",
    title: "Undergraduate Entrance Examinations",
    text: "CUET preparation for undergraduate admission opportunities at participating universities, and applicable MHT-CET programmes based on stream and eligibility.",
  },
  {
    icon: "scale",
    title: "Law Entrance Examinations",
    text: "Preparation for MH CET Law, CLAT, SLAT, LSAT—India where applicable, and AILET.",
  },
  {
    icon: "briefcase",
    title: "BBA Entrance Examinations",
    text: "Preparation for CUET for applicable BBA programmes, SET (Symbiosis Entrance Test), and other relevant university-level BBA entrance examinations.",
  },
];

const FACULTY_POINTS = [
  "Simple and effective explanations",
  "Strong conceptual understanding",
  "Systematic syllabus completion",
  "Examination-oriented practice",
  "Regular doubt-solving",
  "Continuous academic guidance",
];

const ATTENTION_POINTS = [
  "Identifying learning gaps",
  "Individual doubt-solving",
  "Monitoring academic progress",
  "Additional guidance where required",
  "Encouraging regular study habits",
  "Supporting examination preparation",
];

const TEST_POINTS = [
  "Regular subject-wise tests",
  "Chapter-wise assessments",
  "Syllabus coverage monitoring",
  "Revision and practice",
  "Identification of weak areas",
  "Performance analysis and feedback",
  "Board and CA Foundation examination readiness",
];

const ADVANTAGES = [
  {
    icon: "trophy",
    title: "State-Level Topper Achievements",
    text: "Recognised academic performance in CA Foundation and Class 12 Board examinations.",
  },
  {
    icon: "users",
    title: "Experienced Faculty",
    text: "Dedicated educators focused on conceptual clarity.",
  },
  {
    icon: "book",
    title: "Structured Academic Programmes",
    text: "Systematic teaching and syllabus coverage.",
  },
  {
    icon: "user",
    title: "Individual Attention",
    text: "Support according to students' learning needs.",
  },
  {
    icon: "chart",
    title: "Planned Test Schedule",
    text: "Regular assessment and performance monitoring.",
  },
  {
    icon: "target",
    title: "Result-Oriented Preparation",
    text: "Focused Board and CA Foundation examination support.",
  },
  {
    icon: "message",
    title: "Doubt-Solving Support",
    text: "Guidance to strengthen understanding.",
  },
  {
    icon: "briefcase",
    title: "Career-Focused Learning",
    text: "A foundation for commerce, accounting, finance and professional education.",
  },
];

const Icon = ({ name, size = 25 }) => {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };

  const paths = {
    book: (
      <>
        <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3H20v16H6.5A2.5 2.5 0 0 0 4 21Z" />
        <path d="M4 5.5V21M8 7h8M8 11h8" />
      </>
    ),
    award: (
      <>
        <circle cx="12" cy="8" r="6" />
        <path d="m8 13-1 8 5-3 5 3-1-8M9.5 8l1.7 1.7L15 6" />
      </>
    ),
    graduation: (
      <>
        <path d="m2 9 10-5 10 5-10 5Z" />
        <path d="M6 11v5c4 3 8 3 12 0v-5M22 9v7" />
      </>
    ),
    scale: (
      <>
        <path d="M12 3v18M5 7h14M8 7l-4 7h8L8 7Zm8 0-4 7h8l-4-7ZM5 21h14" />
      </>
    ),
    briefcase: (
      <>
        <rect x="3" y="7" width="18" height="14" rx="2" />
        <path d="M8 7V4h8v3M3 12h18M10 12v2h4v-2" />
      </>
    ),
    trophy: (
      <>
        <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0Z" />
        <path d="M7 6H3v2a5 5 0 0 0 5 5M17 6h4v2a5 5 0 0 1-5 5" />
      </>
    ),
    users: (
      <>
        <circle cx="9" cy="8" r="4" />
        <path d="M2 21v-2a7 7 0 0 1 14 0v2M17 4a4 4 0 0 1 0 8M19 15a6 6 0 0 1 3 5v1" />
      </>
    ),
    user: (
      <>
        <circle cx="12" cy="8" r="4" />
        <path d="M4 21a8 8 0 0 1 16 0" />
      </>
    ),
    chart: (
      <>
        <path d="M3 3v18h18" />
        <path d="m7 14 4-4 4 3 5-7" />
      </>
    ),
    target: (
      <>
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5" />
        <circle cx="12" cy="12" r="1" />
      </>
    ),
    message: (
      <>
        <path d="M21 11.5a8.5 8.5 0 0 1-12.5 7.5L3 21l1.8-5.1A8.5 8.5 0 1 1 21 11.5Z" />
        <path d="M8 11h8M8 14h5" />
      </>
    ),
    check: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m8 12 2.5 2.5L16 9" />
      </>
    ),
    calendar: (
      <>
        <rect x="3" y="5" width="18" height="16" rx="2" />
        <path d="M16 3v4M8 3v4M3 10h18M8 14h3M8 17h6" />
      </>
    ),
    compass: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="m15.5 8.5-2 5-5 2 2-5Z" />
      </>
    ),
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      aria-hidden="true"
      {...common}
    >
      {paths[name] || paths.book}
    </svg>
  );
};

const SectionHead = ({ title, sub }) => {
  const words = title.trim().split(" ");
  const lastWord = words.pop();

  return (
    <motion.div
      className="ga-head"
      variants={staggerContainer}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true }}
    >
      <Reveal as={motion.h2} variants={revealUp} className="ga-title">
        {words.length ? `${words.join(" ")} ` : ""}
        <span className="gradient-text">{lastWord}</span>
      </Reveal>

      {sub && (
        <Reveal as={motion.p} variants={revealUp} className="ga-sub">
          {sub}
        </Reveal>
      )}
    </motion.div>
  );
};

const CommerceAcademyShowcase = () => {
  const { colors } = useTheme();

  const rootStyle = {
    "--ga-primary": colors.primary,
    "--ga-primary-dark": colors.primaryDark,
    "--ga-secondary": colors.secondary,
    "--ga-text-light": colors.textLight,
    "--ga-hover": colors.hover,
    "--ga-overlay": colors.overlay,
    "--ga-button-primary": colors.buttonPrimary,
    "--ga-button-text": colors.buttonText,
    "--ga-text": colors.text,
    "--ga-text2": colors.textSecondary,
    "--ga-bg": colors.background,
    "--ga-bg-gradient": colors.backgroundGradient,
    "--ga-bg-alt": colors.backgroundAlt,
    "--ga-card": colors.card,
    "--ga-border": colors.border,
    "--ga-shadow": colors.cardShadow,
  };

  const goTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const renderList = (items) => (
    <div className="ga-commerce-list">
      {items.map((item) => (
        <div className="ga-commerce-list-item" key={item}>
          <span className="ga-commerce-check">
            <Icon name="check" size={18} />
          </span>
          <span>{item}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div className="ga-root ga-commerce-root" style={rootStyle}>
      <style>{`
        .ga-commerce-root {
          background: var(--ga-bg);
          color: var(--ga-text);
          font-family: inherit;
          line-height: 1.6;
          overflow: hidden;
          isolation: isolate;
        }

        .ga-commerce-root * { box-sizing: border-box; }

        .ga-commerce-root .ga-section {
          padding: 4rem 1.5rem;
          position: relative;
          scroll-margin-top: 116px;
        }

        .ga-commerce-root .ga-section--alt {
          background: var(--ga-bg-alt);
        }

        .ga-commerce-root .ga-section--plain {
          background: var(--ga-bg);
        }

        .ga-commerce-root .ga-wrap {
          width: 100%;
          max-width: 1240px;
          margin: 0 auto;
        }

        .ga-commerce-root .ga-head {
          text-align: center;
          margin: 0 auto 2.25rem;
        }

        .ga-commerce-root .ga-title {
          color: var(--ga-text);
          font-size: clamp(1.8rem, 3vw, 2.5rem);
          font-weight: 700;
          line-height: 1.3;
          letter-spacing: -0.025em;
          margin: 0 0 .75rem;
        }

        .ga-commerce-root .gradient-text {
          background: linear-gradient(
            135deg,
            var(--ga-primary) 0%,
            var(--ga-primary-dark, var(--ga-primary)) 100%
          );
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        [data-theme="dark"] .ga-commerce-root .gradient-text {
          background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
          color: transparent;
        }

        .ga-commerce-root .ga-sub {
          color: var(--ga-text2);
          max-width: 820px;
          margin: 0 auto;
          font-size: 1rem;
          line-height: 1.8;
        }

        .ga-commerce-root .ga-grid {
          display: grid;
          gap: 1.5rem;
        }

        .ga-commerce-root .ga-grid-2 {
          grid-template-columns: repeat(2, minmax(0, 1fr));
        }

        .ga-commerce-root .ga-grid-3 {
          grid-template-columns: repeat(3, minmax(0, 1fr));
        }

        .ga-commerce-root .ga-grid-4 {
          grid-template-columns: repeat(4, minmax(0, 1fr));
        }

        .ga-commerce-root .ga-card {
          height: 100%;
          min-width: 0;
          background: var(--ga-card);
          color: var(--ga-text);
          border: 1px solid var(--ga-border);
          border-radius: 1.25rem;
          padding: 1.6rem;
          box-shadow: var(--ga-shadow);
          transition: transform .25s ease, border-color .25s ease;
        }

        .ga-commerce-root .ga-card:hover {
          transform: translateY(-3px);
          border-color: var(--ga-primary);
        }

        .ga-commerce-root .ga-card-title {
          color: var(--ga-text);
          font-size: 1.1rem;
          font-weight: 700;
          line-height: 1.4;
          margin: .85rem 0 .55rem;
        }

        .ga-commerce-root .ga-card-text {
          color: var(--ga-text2);
          font-size: .96rem;
          line-height: 1.75;
          margin: 0;
        }

        .ga-commerce-root .ga-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          color: var(--ga-primary);
          background: var(--ga-bg-alt);
          border: 1px solid var(--ga-border);
          border-radius: 12px;
        }

        .ga-commerce-root .ga-hero {
          background: var(--ga-bg);
          color: var(--ga-text);
          padding-top: 4.5rem;
          padding-bottom: 4.5rem;
        }

        .ga-commerce-root .ga-hero-layout {
          display: grid;
          grid-template-columns: minmax(0, 1.1fr) minmax(0, .9fr);
          gap: 2.5rem;
          align-items: center;
        }

        .ga-commerce-root .ga-eyebrow {
          display: inline-flex;
          align-items: center;
          gap: .5rem;
          color: var(--ga-text2);
          border: 1px solid var(--ga-border);
          border-radius: 999px;
          padding: .45rem .8rem;
          font-size: .88rem;
          margin-bottom: 1rem;
        }

        .ga-commerce-root .ga-hero-title {
          color: var(--ga-text);
          font-size: clamp(2.1rem, 4.2vw, 3.5rem);
          font-weight: 750;
          line-height: 1.16;
          letter-spacing: -.035em;
          margin: 0 0 1rem;
        }

        .ga-commerce-root .ga-hero-lead {
          color: var(--ga-text2);
          font-size: 1.02rem;
          line-height: 1.85;
          margin: 0 0 1rem;
        }

        .ga-commerce-root .ga-hero-image {
          width: 100%;
          min-height: 330px;
          border: 1px solid var(--ga-border);
          border-radius: 1.25rem;
          background: var(--ga-bg-alt);
          box-shadow: var(--ga-shadow);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.5rem;
          text-align: center;
          color: var(--ga-text2);
        }

        .ga-commerce-root .ga-actions {
          display: flex;
          flex-wrap: wrap;
          gap: .8rem;
          margin-top: 1.5rem;
        }

        .ga-commerce-root .ga-btn {
          border: 1px solid var(--ga-border);
          border-radius: .65rem;
          padding: .85rem 1.1rem;
          min-height: 46px;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
          transition: opacity .2s ease, transform .2s ease;
        }

        .ga-commerce-root .ga-btn:hover {
          opacity: .88;
          transform: translateY(-1px);
        }

        .ga-commerce-root .ga-btn-primary {
          background: var(--ga-button-primary, var(--ga-primary));
          color: var(--ga-button-text, #fff);
          border-color: transparent;
        }

        .ga-commerce-root .ga-btn-ghost {
          background: transparent;
          color: var(--ga-text);
        }

        .ga-commerce-root .ga-stat {
          text-align: center;
        }

        .ga-commerce-root .ga-stat-value {
          color: var(--ga-primary);
          font-size: clamp(1.5rem, 2.5vw, 2.1rem);
          font-weight: 750;
          line-height: 1.25;
          margin-bottom: .55rem;
        }

        .ga-commerce-root .ga-stat-label {
          color: var(--ga-text2);
          font-size: .94rem;
          line-height: 1.55;
        }

        .ga-commerce-root .ga-commerce-list {
          display: grid;
          gap: .8rem;
        }

        .ga-commerce-root .ga-commerce-list-item {
          display: flex;
          align-items: flex-start;
          gap: .7rem;
          color: var(--ga-text2);
          line-height: 1.65;
        }

        .ga-commerce-root .ga-commerce-check {
          flex: 0 0 24px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: var(--ga-primary);
          margin-top: .1rem;
        }

        .ga-commerce-root .ga-journey-card {
          position: relative;
          overflow: hidden;
        }

        .ga-commerce-root .ga-year {
          color: var(--ga-primary);
          font-size: 1.05rem;
          font-weight: 750;
          margin-bottom: .65rem;
        }

        .ga-commerce-root .ga-quote {
          margin-top: 1.5rem;
          padding: 1.5rem;
          text-align: center;
          color: var(--ga-text);
          background: var(--ga-card);
          border: 1px solid var(--ga-border);
          border-radius: 1rem;
          box-shadow: var(--ga-shadow);
          font-weight: 650;
          font-size: clamp(1.05rem, 2vw, 1.35rem);
        }

        .ga-commerce-root .ga-commitment {
          padding: 2rem;
          border: 1px solid var(--ga-border);
          border-radius: 1.25rem;
          background: var(--ga-card);
          box-shadow: var(--ga-shadow);
        }

        .ga-commerce-root .ga-admission {
          text-align: center;
        }

        .ga-commerce-root .ga-admission .ga-sub {
          margin-top: .5rem;
        }

        .ga-commerce-root .ga-admission .ga-actions {
          justify-content: center;
        }

        .ga-commerce-root .ga-small-note {
          color: var(--ga-text2);
          font-size: .9rem;
          margin-top: 1rem;
        }

        @media (max-width: 900px) {
          .ga-commerce-root .ga-grid-4 {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }

          .ga-commerce-root .ga-hero-layout {
            grid-template-columns: minmax(0, 1fr);
          }

          .ga-commerce-root .ga-hero-image {
            min-height: 240px;
          }
        }

        @media (max-width: 768px) {
          .ga-commerce-root .ga-section {
            padding: 2.75rem 1rem;
          }

          .ga-commerce-root .ga-head {
            margin-bottom: 1.75rem;
          }

          .ga-commerce-root .ga-title {
            font-size: clamp(1.55rem, 5vw, 1.9rem);
          }

          .ga-commerce-root .ga-sub {
            font-size: .94rem;
          }

          .ga-commerce-root .ga-card {
            padding: 1.2rem;
            border-radius: 14px;
          }

          .ga-commerce-root .ga-grid {
            gap: 1rem;
          }

          .ga-commerce-root .ga-hero-title {
            font-size: clamp(1.85rem, 6vw, 2.5rem);
          }
        }

        @media (max-width: 560px) {
          .ga-commerce-root .ga-section {
            padding: 2.25rem .9rem;
          }

          .ga-commerce-root .ga-grid-2,
          .ga-commerce-root .ga-grid-3,
          .ga-commerce-root .ga-grid-4 {
            grid-template-columns: minmax(0, 1fr);
          }

          .ga-commerce-root .ga-card {
            padding: 1rem;
          }

          .ga-commerce-root .ga-hero-image {
            min-height: 180px;
          }

          .ga-commerce-root .ga-actions .ga-btn {
            width: 100%;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .ga-commerce-root .ga-card,
          .ga-commerce-root .ga-btn {
            transition: none;
          }
        }
      `}</style>

      {/* 01 — HERO */}
      <section id="commerce-hero" className="ga-section ga-hero">
        <div className="ga-wrap ga-hero-layout">
          <div>
            <div className="ga-eyebrow">
              <Icon name="compass" size={18} />
              A Unit of GURUKULM | Gurukul Academy
            </div>

            <h1 className="ga-hero-title">
              Build Your Commerce Foundation. Shape Your Professional Future.
            </h1>

            <p className="ga-hero-lead">
              Learn with Clarity. Prepare with Purpose. Achieve Excellence.
            </p>

            <p className="ga-card-text">
              Gurukul Commerce Academy is committed to developing strong
              commerce fundamentals and preparing students for academic
              excellence and professional careers.
            </p>

            <p className="ga-card-text" style={{ marginTop: "1rem" }}>
              With state-level topper performances in CA Foundation and Class
              12 Board examinations, our approach combines experienced faculty,
              a planned test schedule, individual attention and continuous
              academic guidance.
            </p>

            <div className="ga-actions">
              <button
                className="ga-btn ga-btn-primary"
                onClick={() => goTo("commerce-programmes")}
              >
                Explore Programmes
              </button>

              <button
                className="ga-btn ga-btn-ghost"
                onClick={() => goTo("commerce-admission")}
              >
                Admission Enquiry
              </button>
            </div>
          </div>

          <div
            className="ga-hero-image"
            role="img"
            aria-label="Gurukul Commerce Academy hero image placeholder"
          >
            <div>
              <Icon name="graduation" size={46} />
              <p style={{ margin: ".8rem 0 0" }}>
                Commerce Education · CA Foundation · Professional Careers
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 02 — ACADEMY AT A GLANCE */}
      <section id="commerce-glance" className="ga-section ga-section--alt">
        <div className="ga-wrap">
          <SectionHead
            title="Academy at a Glance"
            sub="Strong commerce fundamentals, structured preparation and focused career direction."
          />

          <motion.div
            className="ga-grid ga-grid-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {[
              {
                value: "2022",
                label: "Established",
                icon: "calendar",
              },
              {
                value: "CA",
                label: "Foundation Preparation",
                icon: "award",
              },
              {
                value: "State-Level",
                label: "Topper Achievements",
                icon: "trophy",
              },
              {
                value: "Multiple",
                label: "Academic & Entrance Pathways",
                icon: "compass",
              },
            ].map((item) => (
              <Reveal
                as={motion.div}
                key={item.label}
                variants={zoomIn}
                className="ga-card ga-stat"
              >
                <div className="ga-icon" style={{ margin: "0 auto .9rem" }}>
                  <Icon name={item.icon} />
                </div>
                <div className="ga-stat-value">{item.value}</div>
                <div className="ga-stat-label">{item.label}</div>
              </Reveal>
            ))}
          </motion.div>

          <p className="ga-card-text" style={{ marginTop: "1.5rem" }}>
            Academic focus: Commerce Education, CA Foundation, MH CET Law,
            CLAT, SLAT, LSAT, AILET, CUET, SET and other relevant university-level
            BBA entrance examinations.
          </p>
        </div>
      </section>

      {/* 03 — OUR JOURNEY */}
      <section id="commerce-journey" className="ga-section ga-section--plain">
        <div className="ga-wrap">
          <SectionHead
            title="Our Journey"
            sub="Excellence in Commerce Education"
          />

          <div className="ga-grid ga-grid-3">
            {[
              {
                year: "2022",
                title: "A Vision for Commerce Education",
                text: "Gurukul Commerce Academy was established with a vision to provide quality commerce education and build a strong foundation for students aspiring to pursue professional careers.",
              },
              {
                year: "Academic Preparation",
                title: "Concepts & Discipline",
                text: "Our approach focuses on conceptual clarity, disciplined preparation, regular assessment and personalised support.",
              },
              {
                year: "Academic Achievement",
                title: "Creating Success Stories",
                text: "State-level topper achievements in CA Foundation and Class 12 Board examinations reflect the dedicated efforts of students and faculty.",
              },
            ].map((item) => (
              <Reveal
                as={motion.div}
                key={item.year}
                variants={zoomIn}
                className="ga-card ga-journey-card"
              >
                <div className="ga-year">{item.year}</div>
                <h3 className="ga-card-title">{item.title}</h3>
                <p className="ga-card-text">{item.text}</p>
              </Reveal>
            ))}
          </div>

          <div className="ga-quote">
            From Classroom Excellence to Professional Aspirations.
          </div>
        </div>
      </section>

      {/* 04 — ACADEMIC PROGRAMMES */}
      <section
        id="commerce-programmes"
        className="ga-section ga-section--alt"
      >
        <div className="ga-wrap">
          <SectionHead
            title="Our Academic Programmes"
            sub="One Academy. Multiple Pathways to Success."
          />

          <p className="ga-card-text" style={{ marginBottom: "1.5rem" }}>
            Gurukul Commerce Academy provides structured academic preparation
            for Class 11 and 12 Commerce students and aspirants targeting
            professional courses and undergraduate entrance examinations.
          </p>

          <div className="ga-grid ga-grid-3">
            {ACADEMIC_PROGRAMMES.map((programme) => (
              <Reveal
                as={motion.div}
                key={programme.title}
                variants={revealUp}
                className="ga-card"
              >
                <div className="ga-icon">
                  <Icon name={programme.icon} />
                </div>
                <h3 className="ga-card-title">{programme.title}</h3>
                <p className="ga-card-text">{programme.text}</p>
              </Reveal>
            ))}
          </div>

          <div className="ga-quote">
            From Commerce Fundamentals to Professional Aspirations—Build Your
            Future with Gurukul.
          </div>
        </div>
      </section>

      {/* 05 — FACULTY */}
      <section id="commerce-faculty" className="ga-section ga-section--plain">
        <div className="ga-wrap">
          <SectionHead
            title="Experienced Faculty"
            sub="Expert Guidance. Clear Concepts. Better Performance."
          />

          <div className="ga-grid ga-grid-2">
            <div className="ga-card">
              <div className="ga-icon">
                <Icon name="users" />
              </div>
              <h3 className="ga-card-title">Dedicated Educators</h3>
              <p className="ga-card-text">
                Our experienced faculty members play a vital role in helping
                students understand challenging concepts and prepare
                effectively for examinations. We encourage students to ask
                questions, clarify doubts and develop confidence.
              </p>
            </div>

            <div className="ga-card">
              <h3 className="ga-card-title">Our Teaching Approach</h3>
              {renderList(FACULTY_POINTS)}
            </div>
          </div>

          <div className="ga-quote">
            Learn from Experience. Grow Through Understanding.
          </div>
        </div>
      </section>

      {/* 06 — INDIVIDUAL ATTENTION */}
      <section
        id="commerce-attention"
        className="ga-section ga-section--alt"
      >
        <div className="ga-wrap">
          <SectionHead
            title="Individual Attention"
            sub="Every Student Matters."
          />

          <div className="ga-grid ga-grid-2">
            <div className="ga-card">
              <div className="ga-icon">
                <Icon name="user" />
              </div>
              <h3 className="ga-card-title">Student-Centred Support</h3>
              <p className="ga-card-text">
                Every student has a different learning pace, strengths and
                academic needs. We emphasise individual attention to help
                students improve their understanding and performance.
              </p>
            </div>

            <div className="ga-card">
              <h3 className="ga-card-title">How We Support Students</h3>
              {renderList(ATTENTION_POINTS)}
            </div>
          </div>

          <div className="ga-quote">
            Personal Attention. Meaningful Progress. Academic Excellence.
          </div>
        </div>
      </section>

      {/* 07 — TEST SCHEDULE */}
      <section id="commerce-tests" className="ga-section ga-section--plain">
        <div className="ga-wrap">
          <SectionHead
            title="Structured Test Schedule"
            sub="Assess. Analyse. Improve. Achieve."
          />

          <div className="ga-grid ga-grid-2">
            <div className="ga-card">
              <div className="ga-icon">
                <Icon name="calendar" />
              </div>
              <h3 className="ga-card-title">Planned Assessment</h3>
              <p className="ga-card-text">
                Consistent practice and regular assessment help students
                evaluate their preparation, identify areas for improvement and
                become familiar with examination expectations.
              </p>
            </div>

            <div className="ga-card">
              <h3 className="ga-card-title">Assessment Process</h3>
              {renderList(TEST_POINTS)}
            </div>
          </div>

          <div className="ga-quote">
            Every Test Is an Opportunity to Improve.
          </div>
        </div>
      </section>

      {/* 08 — RESULT-ORIENTED LEARNING */}
      <section
        id="commerce-results"
        className="ga-section ga-section--alt"
      >
        <div className="ga-wrap">
          <SectionHead
            title="Result-Oriented Learning"
            sub="A Focus on Academic Achievement"
          />

          <div className="ga-grid ga-grid-2">
            <div className="ga-card">
              <div className="ga-icon">
                <Icon name="trophy" />
              </div>
              <h3 className="ga-card-title">
                State-Level Topper Achievements
              </h3>
              <p className="ga-card-text">
                Our state-level topper achievements in CA Foundation and Class
                12 Board examinations demonstrate our commitment to nurturing
                student potential and striving for excellence.
              </p>
            </div>

            <div className="ga-card">
              <div className="ga-icon">
                <Icon name="target" />
              </div>
              <h3 className="ga-card-title">Consistent Preparation</h3>
              <p className="ga-card-text">
                We combine quality teaching, structured preparation and
                regular evaluation. We believe clear concepts, disciplined
                preparation, consistent effort and timely guidance form the
                foundation of meaningful results.
              </p>
            </div>
          </div>

          <div className="ga-quote">
            Focused Preparation. Consistent Effort. Outstanding Achievement.
          </div>
        </div>
      </section>

      {/* 09 — WHY CHOOSE GURUKUL */}
      <section id="commerce-advantages" className="ga-section ga-section--plain">
        <div className="ga-wrap">
          <SectionHead
            title="Why Choose Gurukul Commerce Academy?"
            sub="The right foundation for academic achievement and professional aspirations."
          />

          <div className="ga-grid ga-grid-4">
            {ADVANTAGES.map((item) => (
              <Reveal
                as={motion.div}
                key={item.title}
                variants={zoomIn}
                className="ga-card"
              >
                <div className="ga-icon">
                  <Icon name={item.icon} />
                </div>
                <h3 className="ga-card-title">{item.title}</h3>
                <p className="ga-card-text">{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 10 — OUR COMMITMENT */}
      <section
        id="commerce-commitment"
        className="ga-section ga-section--alt"
      >
        <div className="ga-wrap">
          <SectionHead
            title="Our Commitment"
            sub="Building Foundations for Professional Success"
          />

          <div className="ga-commitment">
            <p className="ga-card-text">
              We believe that a successful career begins with the right
              foundation. At Gurukul Commerce Academy, we strive to equip
              students with strong subject knowledge, disciplined study habits,
              confidence and the academic preparation required to pursue their
              aspirations.
            </p>

            <p className="ga-card-text" style={{ marginTop: "1rem" }}>
              Whether students are preparing for their Class 12 Board
              examinations or taking their first step towards Chartered
              Accountancy, our commitment remains the same: quality education,
              personal attention and purposeful preparation.
            </p>

            <div className="ga-quote">
              Learn with Purpose. Excel with Confidence. Build Your Future.
            </div>
          </div>
        </div>
      </section>

      {/* 11 — ADMISSIONS */}
      <section
        id="commerce-admission"
        className="ga-section ga-section--plain ga-admission"
      >
        <div className="ga-wrap">
          <div className="ga-icon" style={{ margin: "0 auto 1rem" }}>
            <Icon name="graduation" size={30} />
          </div>

          <SectionHead
            title="Admissions Open"
            sub="Your Commerce Journey Starts Here."
          />

          <p className="ga-card-text">
            Looking for focused preparation for Class 11 and 12 Commerce or CA
            Foundation? Join Gurukul Commerce Academy for experienced faculty,
            structured teaching, regular tests and individual academic support.
          </p>

          <div className="ga-actions">
            <button
              className="ga-btn ga-btn-primary"
              onClick={() => goTo("commerce-programmes")}
            >
              Explore Programmes
            </button>
          </div>

          <p className="ga-small-note">
            GURUKUL COMMERCE ACADEMY · A Unit of GURUKULM | Gurukul Academy
          </p>

          <p className="ga-small-note">
            Contact the Academy for Programme Details, Fees and Admissions.
          </p>

          <div className="ga-quote">Strong Foundations. Ambitious Futures.</div>
        </div>
      </section>
    </div>
  );
};

export default CommerceAcademyShowcase;